import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchBusinessHours,
  updateBusinessHours,
  fetchServiceHours,
  updateServiceHours,
  fetchAvailabilityExceptions,
  createAvailabilityException,
  deleteAvailabilityException,
  fetchSchedulingSettings,
  updateSchedulingSettings,
  fetchAppointmentTypes,
  createAppointmentType,
  updateAppointmentType,
  deleteAppointmentType,
} from '../api';
import './schedulingsettingsmodal.css';

const TABS = [
  { key: 'hours', label: 'Business Hours' },
  { key: 'servicehours', label: 'Service Hours' },
  { key: 'blocked', label: 'Blocked Dates' },
  { key: 'rules', label: 'General' },
  { key: 'types', label: 'Service Types' },
];

const emptyException = { date: '', is_closed: true, open_time: '', close_time: '', reason: '' };
const emptyType = {
  name: '', description: '', starting_price: '', price_unit: 'flat',
  estimated_turnaround_display: '', turnaround_days: 1, work_order_service: 'Other',
  capacity: 1, min_lead_time_hours: 24, booking_window_days: 30, slot_interval_minutes: '',
  is_active: true, display_order: 0,
};

// Mirrors WorkOrder.SERVICE_CHOICES (backend/WorkOrder/models.py) — used to map a
// service onto the right prefilled value when an appointment is pushed to a work order.
const WORK_ORDER_SERVICE_CHOICES = ['Restoration', 'Repair Crack', 'Powder Coating', 'Straightening', 'Mount & Balance', 'Other'];

const SchedulingSettingsModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('hours');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [businessHours, setBusinessHours] = useState([]);
  const [serviceHoursTypeId, setServiceHoursTypeId] = useState(null);
  const [serviceHours, setServiceHours] = useState([]);
  const [serviceHoursLoading, setServiceHoursLoading] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState(emptyException);
  const [settings, setSettings] = useState(null);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [newType, setNewType] = useState(emptyType);
  const [showNewType, setShowNewType] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date();
      const future = new Date();
      future.setDate(future.getDate() + 180);
      const fmt = (d) => d.toISOString().slice(0, 10);

      const [hours, exc, settingsRes, types] = await Promise.all([
        fetchBusinessHours(),
        fetchAvailabilityExceptions(fmt(today), fmt(future)),
        fetchSchedulingSettings(),
        fetchAppointmentTypes(),
      ]);
      setBusinessHours(hours || []);
      setExceptions(exc || []);
      setSettings(settingsRes);
      setAppointmentTypes(types || []);
      setServiceHoursTypeId((prev) => prev || (types && types.length > 0 ? types[0].id : null));
    } catch (err) {
      console.error('Error loading scheduling settings:', err);
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!serviceHoursTypeId) return;
    setServiceHoursLoading(true);
    fetchServiceHours(serviceHoursTypeId)
      .then((rows) => setServiceHours(rows || []))
      .catch((err) => {
        console.error('Error loading service hours:', err);
        setError('Failed to load service hours.');
      })
      .finally(() => setServiceHoursLoading(false));
  }, [serviceHoursTypeId]);

  // --- Business Hours ---
  const handleHoursChange = (id, field, value) => {
    setBusinessHours((prev) => prev.map((h) => (h.id === id ? { ...h, [field]: value } : h)));
  };

  const handleHoursSave = async (row) => {
    try {
      await updateBusinessHours(row.id, {
        is_closed: row.is_closed,
        open_time: row.is_closed ? null : row.open_time,
        close_time: row.is_closed ? null : row.close_time,
      });
    } catch (err) {
      console.error('Error saving business hours:', err);
      setError('Failed to save business hours.');
    }
  };

  // --- Service Hours (per appointment type) ---
  const handleServiceHoursChange = (id, field, value) => {
    setServiceHours((prev) => prev.map((h) => (h.id === id ? { ...h, [field]: value } : h)));
  };

  const handleServiceHoursSave = async (row) => {
    try {
      const updated = await updateServiceHours(row.id, {
        is_closed: row.is_closed,
        open_time: row.is_closed ? null : row.open_time,
        close_time: row.is_closed ? null : row.close_time,
      });
      setServiceHours((prev) => prev.map((h) => (h.id === updated.id ? updated : h)));
    } catch (err) {
      console.error('Error saving service hours:', err);
      setError('Failed to save service hours.');
    }
  };

  const handleCopyShopHours = () => {
    setServiceHours((prev) => prev.map((row) => {
      const bh = businessHours.find((b) => b.weekday === row.weekday);
      if (!bh) return row;
      return { ...row, is_closed: bh.is_closed, open_time: bh.open_time, close_time: bh.close_time };
    }));
  };

  // --- Blocked Dates ---
  const handleAddException = async (e) => {
    e.preventDefault();
    if (!newException.date) return;
    try {
      const payload = {
        date: newException.date,
        is_closed: newException.is_closed,
        open_time: newException.is_closed ? null : newException.open_time || null,
        close_time: newException.is_closed ? null : newException.close_time || null,
        reason: newException.reason,
      };
      const created = await createAvailabilityException(payload);
      setExceptions((prev) => [...prev, created].sort((a, b) => a.date.localeCompare(b.date)));
      setNewException(emptyException);
    } catch (err) {
      console.error('Error adding blocked date:', err);
      setError('Failed to add blocked date.');
    }
  };

  const handleDeleteException = async (id) => {
    try {
      await deleteAvailabilityException(id);
      setExceptions((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error('Error deleting blocked date:', err);
      setError('Failed to delete blocked date.');
    }
  };

  // --- Settings singleton ---
  const handleSettingsChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSettingsSave = async () => {
    try {
      const updated = await updateSchedulingSettings({
        slot_interval_minutes: Number(settings.slot_interval_minutes),
        staff_notifications_enabled: settings.staff_notifications_enabled,
        staff_notification_email: settings.staff_notification_email.trim(),
      });
      setSettings(updated);
      setError(null);
    } catch (err) {
      console.error('Error saving scheduling settings:', err);
      setError(err.response?.data?.staff_notification_email
        ? 'Enter a valid email address for booking notifications.'
        : 'Failed to save scheduling settings.');
    }
  };

  // --- Appointment Types ---
  const handleTypeFieldChange = (id, field, value) => {
    setAppointmentTypes((prev) => prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const handleTypeSave = async (type) => {
    try {
      const updated = await updateAppointmentType(type.id, {
        name: type.name,
        description: type.description,
        starting_price: type.starting_price,
        price_unit: type.price_unit,
        estimated_turnaround_display: type.estimated_turnaround_display,
        turnaround_days: Number(type.turnaround_days),
        work_order_service: type.work_order_service,
        color: type.color,
        capacity: Number(type.capacity),
        min_lead_time_hours: Number(type.min_lead_time_hours),
        booking_window_days: Number(type.booking_window_days),
        slot_interval_minutes: type.slot_interval_minutes ? Number(type.slot_interval_minutes) : null,
        is_active: type.is_active,
        display_order: Number(type.display_order),
      });
      setAppointmentTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) {
      console.error('Error saving appointment type:', err);
      setError('Failed to save service type.');
    }
  };

  const handleTypeDelete = async (id) => {
    if (!window.confirm('Delete this service type? This will fail if appointments reference it — deactivate instead if unsure.')) return;
    try {
      await deleteAppointmentType(id);
      setAppointmentTypes((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error('Error deleting appointment type:', err);
      const msg = err.response?.status === 409 || err.response?.status === 400
        ? 'This service type has existing appointments and cannot be deleted. Deactivate it instead.'
        : 'Failed to delete service type.';
      setError(msg);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    try {
      const created = await createAppointmentType({
        ...newType,
        starting_price: newType.starting_price || '0',
        turnaround_days: Number(newType.turnaround_days) || 0,
        capacity: Number(newType.capacity) || 1,
        min_lead_time_hours: Number(newType.min_lead_time_hours) || 0,
        booking_window_days: Number(newType.booking_window_days) || 30,
        slot_interval_minutes: newType.slot_interval_minutes ? Number(newType.slot_interval_minutes) : null,
        display_order: Number(newType.display_order) || 0,
      });
      setAppointmentTypes((prev) => [...prev, created]);
      setNewType(emptyType);
      setShowNewType(false);
    } catch (err) {
      console.error('Error creating appointment type:', err);
      setError('Failed to create service type.');
    }
  };

  return (
    <div className="scheduling-settings-overlay" onClick={onClose}>
      <div className="scheduling-settings-content" onClick={(e) => e.stopPropagation()}>
        <div className="scheduling-settings-header">
          <h2>Scheduling Settings</h2>
          <button type="button" className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="scheduling-settings-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`scheduling-settings-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="scheduling-settings-error">{error}</div>}

        {loading ? (
          <div className="scheduling-settings-loading">Loading…</div>
        ) : (
          <div className="scheduling-settings-body">
            {activeTab === 'hours' && (
              <div className="scheduling-table-scroll">
                <table className="scheduling-settings-table">
                  <thead>
                    <tr><th>Day</th><th>Closed</th><th>Open</th><th>Close</th><th></th></tr>
                  </thead>
                  <tbody>
                    {businessHours.map((row) => (
                      <tr key={row.id}>
                        <td>{row.weekday_display}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.is_closed}
                            onChange={(e) => handleHoursChange(row.id, 'is_closed', e.target.checked)}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={row.open_time || ''}
                            disabled={row.is_closed}
                            onChange={(e) => handleHoursChange(row.id, 'open_time', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={row.close_time || ''}
                            disabled={row.is_closed}
                            onChange={(e) => handleHoursChange(row.id, 'close_time', e.target.value)}
                          />
                        </td>
                        <td>
                          <button className="scheduling-settings-save-row" onClick={() => handleHoursSave(row)}>Save</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'servicehours' && (
              <div>
                <div className="scheduling-settings-inline-form">
                  <label className="scheduling-settings-checkbox-label">
                    Service:
                    <select
                      value={serviceHoursTypeId || ''}
                      onChange={(e) => setServiceHoursTypeId(Number(e.target.value))}
                    >
                      {appointmentTypes.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="scheduling-settings-save-row" onClick={handleCopyShopHours}>
                    Copy shop hours
                  </button>
                </div>
                <p className="scheduling-settings-hint">
                  This service is only bookable when both the shop and this service are open —
                  narrow individual days below to offer this service for fewer hours than the shop overall.
                  Click "Copy shop hours" then Save to reset a day back to matching the shop.
                </p>

                {serviceHoursLoading ? (
                  <div className="scheduling-settings-loading">Loading…</div>
                ) : (
                  <div className="scheduling-table-scroll">
                    <table className="scheduling-settings-table">
                      <thead>
                        <tr><th>Day</th><th>Closed</th><th>Open</th><th>Close</th><th></th></tr>
                      </thead>
                      <tbody>
                        {serviceHours.map((row) => (
                          <tr key={row.id}>
                            <td>{row.weekday_display}</td>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.is_closed}
                                onChange={(e) => handleServiceHoursChange(row.id, 'is_closed', e.target.checked)}
                              />
                            </td>
                            <td>
                              <input
                                type="time"
                                value={row.open_time || ''}
                                disabled={row.is_closed}
                                onChange={(e) => handleServiceHoursChange(row.id, 'open_time', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="time"
                                value={row.close_time || ''}
                                disabled={row.is_closed}
                                onChange={(e) => handleServiceHoursChange(row.id, 'close_time', e.target.value)}
                              />
                            </td>
                            <td>
                              <button className="scheduling-settings-save-row" onClick={() => handleServiceHoursSave(row)}>Save</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blocked' && (
              <div>
                <form className="scheduling-settings-inline-form" onSubmit={handleAddException}>
                  <input
                    type="date"
                    value={newException.date}
                    onChange={(e) => setNewException((p) => ({ ...p, date: e.target.value }))}
                    required
                  />
                  <label className="scheduling-settings-checkbox-label">
                    <input
                      type="checkbox"
                      checked={newException.is_closed}
                      onChange={(e) => setNewException((p) => ({ ...p, is_closed: e.target.checked }))}
                    />
                    Closed all day
                  </label>
                  {!newException.is_closed && (
                    <>
                      <input
                        type="time"
                        value={newException.open_time}
                        onChange={(e) => setNewException((p) => ({ ...p, open_time: e.target.value }))}
                        placeholder="Open"
                      />
                      <input
                        type="time"
                        value={newException.close_time}
                        onChange={(e) => setNewException((p) => ({ ...p, close_time: e.target.value }))}
                        placeholder="Close"
                      />
                    </>
                  )}
                  <input
                    type="text"
                    value={newException.reason}
                    onChange={(e) => setNewException((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Reason (optional)"
                  />
                  <button type="submit" className="scheduling-settings-add-btn">Add</button>
                </form>

                <ul className="scheduling-settings-list">
                  {exceptions.map((exc) => (
                    <li key={exc.id}>
                      <span>
                        <strong>{exc.date}</strong> — {exc.is_closed ? 'Closed' : `${exc.open_time}–${exc.close_time}`}
                        {exc.reason ? ` (${exc.reason})` : ''}
                      </span>
                      <button className="scheduling-settings-delete-btn" onClick={() => handleDeleteException(exc.id)}>Remove</button>
                    </li>
                  ))}
                  {exceptions.length === 0 && <li className="scheduling-settings-empty">No blocked dates upcoming.</li>}
                </ul>
              </div>
            )}

            {activeTab === 'rules' && settings && (
              <div className="scheduling-settings-rules">
                <label>
                  Default slot interval (minutes)
                  <input
                    type="number"
                    min="5"
                    value={settings.slot_interval_minutes}
                    onChange={(e) => handleSettingsChange('slot_interval_minutes', e.target.value)}
                  />
                </label>
                <p className="scheduling-settings-hint">
                  The slot length used by any service that doesn&apos;t set its own. Each service
                  can override it, along with capacity and booking rules (lead time, booking
                  window), on the Service Types tab.
                </p>

                <label className="scheduling-settings-checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.staff_notifications_enabled}
                    onChange={(e) => handleSettingsChange('staff_notifications_enabled', e.target.checked)}
                  />
                  Email the shop about online bookings
                </label>
                <label>
                  Send booking notifications to
                  <input
                    type="email"
                    value={settings.staff_notification_email}
                    disabled={!settings.staff_notifications_enabled}
                    onChange={(e) => handleSettingsChange('staff_notification_email', e.target.value)}
                  />
                </label>
                <p className="scheduling-settings-hint">
                  A short email when a customer books, changes or cancels on the website.
                  Changes made here in the app don&apos;t send one. Replying goes straight to
                  the customer.
                </p>
                <button className="scheduling-settings-save-btn" onClick={handleSettingsSave}>Save</button>
              </div>
            )}

            {activeTab === 'types' && (
              <div>
                <div className="scheduling-table-scroll">
                <table className="scheduling-settings-table scheduling-types-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Color</th><th>Price</th><th>Unit</th>
                      <th>Turnaround</th><th>Turnaround (days)</th><th>Work Order Service</th>
                      <th>Capacity</th><th>Lead Time (hrs)</th><th>Booking Window (days)</th><th>Slot Interval (min)</th>
                      <th>Active</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointmentTypes.map((t) => (
                      <tr key={t.id}>
                        <td><input type="text" value={t.name} onChange={(e) => handleTypeFieldChange(t.id, 'name', e.target.value)} /></td>
                        <td>
                          <input
                            type="color"
                            className="scheduling-settings-color-input"
                            value={t.color || '#666666'}
                            onChange={(e) => handleTypeFieldChange(t.id, 'color', e.target.value)}
                          />
                        </td>
                        <td><input type="number" step="0.01" value={t.starting_price} onChange={(e) => handleTypeFieldChange(t.id, 'starting_price', e.target.value)} /></td>
                        <td>
                          <select value={t.price_unit} onChange={(e) => handleTypeFieldChange(t.id, 'price_unit', e.target.value)}>
                            <option value="flat">Flat</option>
                            <option value="per_wheel">Per Wheel</option>
                          </select>
                        </td>
                        <td><input type="text" value={t.estimated_turnaround_display} onChange={(e) => handleTypeFieldChange(t.id, 'estimated_turnaround_display', e.target.value)} /></td>
                        <td><input type="number" min="0" value={t.turnaround_days} onChange={(e) => handleTypeFieldChange(t.id, 'turnaround_days', e.target.value)} /></td>
                        <td>
                          <select value={t.work_order_service} onChange={(e) => handleTypeFieldChange(t.id, 'work_order_service', e.target.value)}>
                            {WORK_ORDER_SERVICE_CHOICES.map((choice) => (
                              <option key={choice} value={choice}>{choice}</option>
                            ))}
                          </select>
                        </td>
                        <td><input type="number" min="1" value={t.capacity} onChange={(e) => handleTypeFieldChange(t.id, 'capacity', e.target.value)} /></td>
                        <td><input type="number" min="0" value={t.min_lead_time_hours} onChange={(e) => handleTypeFieldChange(t.id, 'min_lead_time_hours', e.target.value)} /></td>
                        <td><input type="number" min="1" value={t.booking_window_days} onChange={(e) => handleTypeFieldChange(t.id, 'booking_window_days', e.target.value)} /></td>
                        <td>
                          <input
                            type="number"
                            min="5"
                            placeholder={settings ? `Default (${settings.slot_interval_minutes})` : 'Default'}
                            value={t.slot_interval_minutes ?? ''}
                            onChange={(e) => handleTypeFieldChange(t.id, 'slot_interval_minutes', e.target.value)}
                          />
                        </td>
                        <td><input type="checkbox" checked={t.is_active} onChange={(e) => handleTypeFieldChange(t.id, 'is_active', e.target.checked)} /></td>
                        <td>
                          <button className="scheduling-settings-save-row" onClick={() => handleTypeSave(t)}>Save</button>
                          <button className="scheduling-settings-delete-btn" onClick={() => handleTypeDelete(t.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {!showNewType ? (
                  <button className="scheduling-settings-add-btn" onClick={() => setShowNewType(true)}>+ Add Service Type</button>
                ) : (
                  <form className="scheduling-settings-inline-form" onSubmit={handleAddType}>
                    <input type="text" placeholder="Name" value={newType.name} onChange={(e) => setNewType((p) => ({ ...p, name: e.target.value }))} required />
                    <input type="text" placeholder="Description" value={newType.description} onChange={(e) => setNewType((p) => ({ ...p, description: e.target.value }))} />
                    <input type="number" step="0.01" placeholder="Price" value={newType.starting_price} onChange={(e) => setNewType((p) => ({ ...p, starting_price: e.target.value }))} required />
                    <select value={newType.price_unit} onChange={(e) => setNewType((p) => ({ ...p, price_unit: e.target.value }))}>
                      <option value="flat">Flat</option>
                      <option value="per_wheel">Per Wheel</option>
                    </select>
                    <input type="text" placeholder="Turnaround text" value={newType.estimated_turnaround_display} onChange={(e) => setNewType((p) => ({ ...p, estimated_turnaround_display: e.target.value }))} />
                    <input type="number" min="0" placeholder="Turnaround (days)" value={newType.turnaround_days} onChange={(e) => setNewType((p) => ({ ...p, turnaround_days: e.target.value }))} />
                    <select value={newType.work_order_service} onChange={(e) => setNewType((p) => ({ ...p, work_order_service: e.target.value }))}>
                      {WORK_ORDER_SERVICE_CHOICES.map((choice) => (
                        <option key={choice} value={choice}>{choice}</option>
                      ))}
                    </select>
                    <input type="number" min="1" placeholder="Capacity" value={newType.capacity} onChange={(e) => setNewType((p) => ({ ...p, capacity: e.target.value }))} />
                    <input type="number" min="0" placeholder="Lead time (hrs)" value={newType.min_lead_time_hours} onChange={(e) => setNewType((p) => ({ ...p, min_lead_time_hours: e.target.value }))} />
                    <input type="number" min="1" placeholder="Booking window (days)" value={newType.booking_window_days} onChange={(e) => setNewType((p) => ({ ...p, booking_window_days: e.target.value }))} />
                    <input type="number" min="5" placeholder="Slot interval (min, blank = default)" value={newType.slot_interval_minutes} onChange={(e) => setNewType((p) => ({ ...p, slot_interval_minutes: e.target.value }))} />
                    <button type="submit" className="scheduling-settings-add-btn">Create</button>
                    <button type="button" className="scheduling-settings-delete-btn" onClick={() => setShowNewType(false)}>Cancel</button>
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulingSettingsModal;
